import {COLOR_RED, generateUniqueId, levelData, creatureTypes, dndblevelDataArray, creatureSizes, getConditionImmunities, getAdjustments, dndbEnvironments } from '../constants';

function removeHTMLTags(str) {
    return str.replace(/<\/?[^>]+(>|$)/g, "");
}

function getPassives(res) {
    let passivePerception = res.passivePerception || "";
    let passiveInvestigation = res.passiveInvestigation || "";
    let passiveInsight = res.passiveInsight || "";

    let returnstring = ""
    
    if(passivePerception) {
        returnstring += "Passive Perception " + passivePerception + " "
    }
    if(passiveInvestigation) {
        returnstring += "Passive Investigation " + passiveInvestigation + " "
    }
    if(passiveInsight) {
        returnstring += "Passive Insight " + passiveInsight + " "
    }

    return returnstring.trim()
}

export function extractLegendaryDescText(htmlString) {
    if (!htmlString || typeof htmlString !== 'string') return '';

    // Find the first <p>...</p>
    const match = htmlString.match(/<p\b[^>]*>([\s\S]*?)<\/p>/i);
    if (!match) return '';
    
    let inner = match[1];
    
    // If the first <p> contains a <strong> tag, return empty string
    if (/<strong\b/i.test(inner)) {
        return '';
    }

    // Remove any remaining HTML tags
    inner = inner.replace(/<\/?[^>]+(>|$)/g, '');

    // Decode common HTML entities. In browser environments use DOM decoding.
    if (typeof document !== 'undefined') {
        const txt = document.createElement('textarea');
        txt.innerHTML = inner;
        return txt.value.trim();
    }

    // Fallback decoding for non-browser environments
    inner = inner.replace(/&#(\d+);/g, (_m, n) => String.fromCharCode(n));
    const entityMap = {
        'nbsp': ' ',
        'amp': '&',
        'lt': '<',
        'gt': '>',
        'quot': '"',
        'apos': "'"
    };
    inner = inner.replace(/&([a-zA-Z]+);/g, (m, n) => entityMap[n] || m);

    return inner.trim();
}

const parseActionsFromHtml = (htmlString) => {
    if (!htmlString || typeof htmlString !== 'string') {
        return [];
    }

    const actions = [];

    // Create a temporary DOM element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;

    // Find all paragraph elements that contain actions
    const paragraphs = tempDiv.querySelectorAll('p');

    paragraphs.forEach(paragraph => {
        // Look for strong tags that indicate action names
        const strongElement = paragraph.querySelector('strong');
        const emElement = paragraph.querySelector('em strong');

        let actionName = '';
        let description = '';

        if (emElement) {
            actionName = emElement.textContent.trim();
            emElement.remove();  // Remove the strong element
            const tooltipLinks = paragraph.querySelectorAll('a.tooltip-hover.spell-tooltip');
            tooltipLinks.forEach(link => {
                const spellName = link.textContent.trim();
                link.replaceWith(document.createTextNode(`{@hover ${spellName}}`));
            });
            description = paragraph.textContent.trim();
        } else if (strongElement) {
            actionName = strongElement.textContent.trim();
            strongElement.remove();  // Remove the strong element
            const tooltipLinks = paragraph.querySelectorAll('a.tooltip-hover.spell-tooltip');
            tooltipLinks.forEach(link => {
                const spellName = link.textContent.trim();
                link.replaceWith(document.createTextNode(`{@hover ${spellName}}`));
            });
            description = paragraph.textContent.trim();
        }

        // Clean up the action name (remove trailing dots, etc.)
        actionName = actionName.replace(/\.$/, '').trim();

        if (actionName && description) {
            // Clean up dice notation spans
            description = description.replace(/<span[^>]*data-dicenotation="([^"]*)"[^>]*>([^<]*)<\/span>/g, '$2');

            // Convert common D&D notation to the desired format
            description = description.replaceAll(/Melee Weapon Attack:/gi, '{@atk mw}')
            description = description.replaceAll(/Melee Attack Roll:/gi, '{@atk mw}')
            description = description.replaceAll(/Ranged Weapon Attack:/gi, '{@atk rw}')
            description = description.replaceAll(/Ranged Attack Roll:/gi, '{@atk rw}')
            description = description.replaceAll(/\((\d+d\d+\s*[+-]\s*\d+)\)/gi, '({@damage $1})') // (1d6 + 2)
            description = description.replaceAll(/\((\d+d\d+)\)/gi, '({@damage $1})') // (1d6)
            description = description.replaceAll(/\+(\d{1,2}) to hit/gi, '{@hit $1} to hit') 
            description = description.replaceAll(/\+(\d{1,2})/gi, '{@hit $1}') 
            description = description.replaceAll(/\[rollable\](.*)?;/gi, '$1')
            description = description.replaceAll(/\{".*?"\}/gi, '')
            description = description.replaceAll(/\[\/rollable\]/gi, '')
            description = description.replaceAll(/Hit:\s/gi, '{@h}') // {@h}5 -> bold Hit: 5

            actions.push({
                "name": actionName,
                "entries": [description],
                "rechargeCount": 0
            });
        }
    });

    return actions;
};

function getTextFromNode(node) {
    const temp = document.createElement('div');
    temp.innerHTML = node.innerHTML;
    return temp.textContent || temp.innerText || '';
}

function normalizeTraitText(text) {
    return text.replace(/\s+/g, ' ').trim();
}

function formatSpellEntry(text) {
    const trimmed = text.trim().replace(/\.$/, '');
    const match = trimmed.match(/^(.+?)(\s*\(.*\))?$/);
    if (!match) return trimmed;
    const spellName = match[1].trim();
    const suffix = match[2] || '';
    return `${spellName ? `{@spell ${spellName}}` : ''}${suffix}`;
}

function parseSpellList(text) {
    return text
        .split(/,\s*/)
        .map(item => item.trim())
        .filter(Boolean)
        .map(formatSpellEntry);
}

function normalizeAbilityName(text) {
    const lower = text.toLowerCase().trim();
    if (lower.startsWith('strength')) return 'str';
    if (lower.startsWith('dexterity')) return 'dex';
    if (lower.startsWith('constitution')) return 'con';
    if (lower.startsWith('intelligence')) return 'int';
    if (lower.startsWith('wisdom')) return 'wis';
    if (lower.startsWith('charisma')) return 'cha';
    return lower;
}

function parseSpellcastingTrait(fullText) {
    const text = normalizeTraitText(fullText);
    const abilityMatch = text.match(/spellcasting ability is\s+([a-z]+)/i);
    const ability = abilityMatch ? normalizeAbilityName(abilityMatch[1]) : '';

    const headerMatch = text.match(/^(.*?)(?:He has the following spells prepared:|The following spells prepared:|Cantrips)/i);
    const headerEntries = headerMatch ? normalizeTraitText(headerMatch[1]) : '';

    const spells = {};

    const cantripMatch = text.match(/(?:Cantrips|0-level)\s*\(at will\):\s*([^\.]+)/i);
    if (cantripMatch) {
        const cantripList = parseSpellList(cantripMatch[1]);
        if (cantripList.length) {
            spells['0'] = {
                slots: null,
                spells: cantripList
            };
        }
    }

    const levelRegex = /(\d+)(?:st|nd|rd|th) level[^:]*:\s*([^\.]+)\./gi;
    let levelMatch;
    while ((levelMatch = levelRegex.exec(text)) !== null) {
        const level = levelMatch[1];
        const spellList = parseSpellList(levelMatch[2]);
        if (spellList.length) {
            spells[level] = {
                slots: null,
                spells: spellList
            };
        }
    }

    return {
        name: 'Spellcasting',
        type: 'spellcasting',
        spells,
        ability,
        displayAs: 'trait',
        headerEntries
    };
}

export const parseSpecialTraitsFromHtml = (htmlString) => {
    if (!htmlString || typeof htmlString !== 'string') {
        return [];
    }

    const root = document.createElement('div');
    root.innerHTML = htmlString;

    const elements = Array.from(root.children);
    const traits = [];
    let i = 0;

    const detectTraitName = (paragraph) => {
        const paragraphText = normalizeTraitText(getTextFromNode(paragraph));
        const firstElement = paragraph.firstElementChild;

        if (firstElement && ['STRONG', 'B', 'EM', 'I'].includes(firstElement.tagName)) {
            const candidate = normalizeTraitText(firstElement.textContent || '');
            const stripped = candidate.replace(/[\.:;]$/, '');
            if (stripped && paragraphText.startsWith(candidate)) {
                return {
                    traitName: stripped,
                    remainingText: normalizeTraitText(paragraphText.slice(candidate.length))
                };
            }
        }

        const headingMatch = paragraphText.match(/^([^\.\;\:]+)[\.\;\:]+\s*(.*)$/);
        if (headingMatch) {
            return {
                traitName: normalizeTraitText(headingMatch[1]),
                remainingText: normalizeTraitText(headingMatch[2])
            };
        }

        return { traitName: null, remainingText: paragraphText };
    };

    while (i < elements.length) {
        const element = elements[i];
        if (element.tagName !== 'P') {
            i++;
            continue;
        }

        const { traitName, remainingText } = detectTraitName(element);
        let finalText = remainingText;
        let nextIndex = i + 1;

        while (nextIndex < elements.length) {
            const nextElem = elements[nextIndex];
            if (nextElem.tagName === 'P') {
                const { traitName: nextTraitName } = detectTraitName(nextElem);
                if (nextTraitName) break;
            }

            finalText += ' ' + normalizeTraitText(getTextFromNode(nextElem));
            nextIndex++;
        }

        if (traitName && traitName.toLowerCase() === 'spellcasting') {
            traits.push(parseSpellcastingTrait(finalText));
            i = nextIndex;
        } else if (traitName) {
            traits.push({
                name: traitName,
                entries: [finalText],
                desc: ` ${finalText} `,
                rechargeCount: 0
            });
            i = nextIndex;
        } else {
            i++;
        }
    }

    return traits
        .filter(trait => trait.name && trait.name.trim())
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
};

function addRechargeCount(objectsArr) {
    if(objectsArr) 
        return objectsArr.map(obj => {
            const match = obj.name.match(/\((\d+)\/[^)]+\)/); // Match (X/any text) pattern
            obj.rechargeCount = match ? parseInt(match[1]) * 10 : 0; // Set to null if no pattern is found
            return obj;
        });
    else 
        return null
}

function addLegendaryCount(string) {
    if(string === "")
        return 30 
    else {
        // Match "take X legendary actions" return X
        const match = string.match(/take (\d+) legendary actions/);
        return match ? parseInt(match[1]) * 10 : 30; // return 30 by default cause thats typical
    }
}

const getStats = (creature, profBonus) => {

    if(creature.stats && creature.stats.length === 6) {
        const STR = creature.stats[0].value
        const DEX = creature.stats[1].value
        const CON = creature.stats[2].value
        const INT = creature.stats[3].value
        const WIS = creature.stats[4].value
        const CHA = creature.stats[5].value
    
        const STR_MOD = Math.floor((STR-10)/2)
        const DEX_MOD = Math.floor((DEX-10)/2)
        const CON_MOD = Math.floor((CON-10)/2)
        const INT_MOD = Math.floor((INT-10)/2)
        const WIS_MOD = Math.floor((WIS-10)/2)
        const CHA_MOD = Math.floor((CHA-10)/2)
    
        let saves = creature.savingThrows
        return {
            "strength": STR,
            "dexterity": DEX,
            "constitution": CON,
            "intelligence": INT,
            "wisdom": WIS,
            "charisma": CHA,
            "strength_save": saves.some(save => save.statId === 1) ? profBonus + STR_MOD : 0,
            "dexterity_save": saves.some(save => save.statId === 2) ? profBonus + DEX_MOD : 0,
            "constitution_save": saves.some(save => save.statId === 3) ? profBonus + CON_MOD : 0,
            "intelligence_save": saves.some(save => save.statId === 4) ? profBonus + INT_MOD : 0,
            "wisdom_save": saves.some(save => save.statId === 5) ? profBonus + WIS_MOD : 0,
            "charisma_save": saves.some(save => save.statId === 6) ? profBonus + CHA_MOD : 0,
        }
    } else {
        return {
            "strength": 10,
            "dexterity": 10,
            "constitution": 10,
            "intelligence": 10,
            "wisdom": 10,
            "charisma": 10,
            "strength_save": 0,
            "dexterity_save": 0,
            "constitution_save": 0,
            "intelligence_save": 0,
            "wisdom_save": 0,
            "charisma_save": 0,
        }
    }
}

const getSpeed = (movements) => {
    let speedJson = {};
    const types = {
        1: 'walk',
        2: 'burrow',
        3: 'climb',
        4: 'fly',
        5: 'swim',
    }

    movements.forEach(move => {
        let type = types[move.movementId]
        speedJson[type] = move.speed + "ft"
    });

    return speedJson
};


//doesnt work on things you dont own
export const DndBMonsterToDmBMapper = async (monsterRes, encounterMonsterRes, encounterGuid) => {
    const lvlObjsArray = Object.entries(levelData);
    const lvlKeys = Object.keys(levelData);
    let lvlObj = lvlObjsArray[monsterRes.challengeRatingId-2] || undefined
    let profBonus = lvlObj ? lvlObj[1].profBonus : 0 
    const stats = getStats(monsterRes, profBonus)

    //5850942,6522137
    // console.log("===================")
    // console.log("res1", monsterRes.name, monsterRes.environments, monsterRes)

    let legendaryActionsDesc = extractLegendaryDescText(monsterRes.legendaryActionsDescription)

    let returnObj = {
        "name": monsterRes.name,
        "name_default": monsterRes.name,
        "from": "dnd_b",
        "creatureGuid": generateUniqueId(),
        "encounterGuid": encounterGuid,
        "status": monsterRes.isReleased || monsterRes.isHomebrew ? 200 : 500,
        "dnd_b_monster_id": monsterRes.id,
        "link": monsterRes.url,
        "avatarUrl": monsterRes.avatarUrl,
        "hit_points": encounterMonsterRes.maximumHitPoints,
        "hit_points_default": encounterMonsterRes.maximumHitPoints,
        "hit_points_current": encounterMonsterRes.currentHitPoints,
        "hit_points_temp": encounterMonsterRes.temporaryHitPoints,
        "hit_points_override": 0, //dnd beyonds override max hp
        "hit_points_modifier": 0, //dnd beyonds max hp modifier
        "initiative": 0,
        "last_damage": null,
        "alignment": "enemy",
        "groupGuid": null,
        "type": "monster",
        "border": COLOR_RED,
        "effects": [],
        "creature_type": creatureTypes[monsterRes.typeId],
        "inspiration": false,
        "bardic_inspiration": false,
        "armor_class": monsterRes.armorClass || 0,
        "armor_desc": removeHTMLTags(monsterRes.armorClassDescription), 
        "inventory": [],
        "speed": getSpeed(monsterRes.movements),
        "skills": [removeHTMLTags(monsterRes.skillsHtml)],
        "spellcasting": monsterRes.spellcasting && monsterRes.spellcasting.length > 0 ? monsterRes.spellcasting : [],
        "special_abilities": parseSpecialTraitsFromHtml(monsterRes.specialTraitsDescription),
        "hidden": false,
        ...stats,
        "hit_points_formula": monsterRes.hitPointDice.diceString || '',
        "challenge_rating": dndblevelDataArray[monsterRes.challengeRatingId],
        "cr": (monsterRes.challengeRatingId).toString() || '',
        "senses": removeHTMLTags(monsterRes.sensesHtml) + " " + getPassives(monsterRes),
        "languages": monsterRes.languageDescription,
        "actions": addRechargeCount(parseActionsFromHtml(monsterRes.actionsDescription)),
        "reactions": addRechargeCount(parseActionsFromHtml(monsterRes.reactionsDescription)),
        "bonus_actions": addRechargeCount(parseActionsFromHtml(monsterRes.bonusActionsDescription)),
        "legendary_actions_count": addLegendaryCount(legendaryActionsDesc),
        "legendary_actions": addRechargeCount(parseActionsFromHtml(monsterRes.legendaryActionsDescription)),
        "legendary_desc": legendaryActionsDesc,
        "mythic_actions":  addRechargeCount(parseActionsFromHtml(monsterRes.mythicActionsDescription)),
        "mythic_desc": extractLegendaryDescText(monsterRes.mythicActionsDescription),
        "lair_actions": parseActionsFromHtml(monsterRes.lairDescription),
        "regional_effects": [],
        "size": creatureSizes[monsterRes.sizeId],
        "isReleased": monsterRes.isReleased,
        "isHomebrew": monsterRes.isHomebrew,
        "environments": monsterRes.environments.map(envId => dndbEnvironments[envId]).join(', '),
        "death_saves_failure_count": 0,
        "death_saves_success_count": 0,
        "inventory": [],
        'damage_vulnerabilities': getAdjustments(monsterRes.damageAdjustments, "Vulnerability"), // damage vulnerabilities come from the damageAdjustments array
        'damage_resistances': getAdjustments(monsterRes.damageAdjustments, "Resistance"), // damage resistances come from the damageAdjustments array
        'damage_immunities': getAdjustments(monsterRes.damageAdjustments, "Immunity"), // damage immunities come from the damageAdjustments array
        'condition_immunities': getConditionImmunities(monsterRes.conditionImmunities), 
    }

    return returnObj
};

export default DndBMonsterToDmBMapper;